import type { IssueFacts, IssueFactValue, IssueRawChange } from './types';

const nonMaterialKeys = new Set([
  'data_as_of',
  'data_loaded_at',
  'fetchedat',
  'fetched_at',
  'checkedat',
  'checked_at',
  'retrievedat',
  'retrieved_at',
  'retrievaltime',
  'retrieval_time',
  'sourceurl',
  'source_url',
]);

function normalize(value: unknown, key = ''): IssueFactValue | undefined {
  if (nonMaterialKeys.has(key.toLowerCase())) return undefined;
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === 'string') return value.trim().replace(/\s+/g, ' ');
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value))
    return value
      .map((item) => normalize(item))
      .filter((item): item is IssueFactValue => item !== undefined);
  if (typeof value === 'object') {
    const result: Record<string, IssueFactValue> = {};
    for (const [childKey, child] of Object.entries(value)) {
      const normalized = normalize(child, childKey);
      if (normalized !== undefined) result[childKey] = normalized;
    }
    return result;
  }
  return String(value);
}

export function canonicalFacts(value: object): IssueFacts {
  return normalize(value) as IssueFacts;
}

export function stableSerialize(value: IssueFactValue | undefined): string {
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;
  if (value && typeof value === 'object')
    return `{${Object.keys(value)
      .sort()
      .map(
        (key) =>
          `${JSON.stringify(key)}:${stableSerialize((value as Record<string, IssueFactValue>)[key])}`,
      )
      .join(',')}}`;
  return JSON.stringify(value);
}

export function materialHash(facts: IssueFacts) {
  const input = stableSerialize(facts);
  let high = 0x9e3779b1;
  let low = 0x811c9dc5;
  for (let index = 0; index < input.length; index++) {
    const code = input.charCodeAt(index);
    low = Math.imul(low ^ code, 0x01000193) >>> 0;
    high = Math.imul(high ^ code, 0x85ebca6b) >>> 0;
  }
  return `${high.toString(16).padStart(8, '0')}${low.toString(16).padStart(8, '0')}`;
}

export function diffFacts(before: IssueFacts | null, after: IssueFacts | null): IssueRawChange[] {
  const changes: IssueRawChange[] = [];
  walk('', before ?? undefined, after ?? undefined, changes);
  return changes;
}

function walk(
  path: string,
  before: IssueFactValue | undefined,
  after: IssueFactValue | undefined,
  changes: IssueRawChange[],
) {
  if (stableSerialize(before) === stableSerialize(after)) return;
  if (isObject(before) && isObject(after)) {
    for (const key of [...new Set([...Object.keys(before), ...Object.keys(after)])].sort())
      walk(path ? `${path}.${key}` : key, before[key], after[key], changes);
    return;
  }
  changes.push({
    path: path || '$',
    ...(before !== undefined ? { before } : {}),
    ...(after !== undefined ? { after } : {}),
  });
}

function isObject(value: IssueFactValue | undefined): value is Record<string, IssueFactValue> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
